import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { attributionColumns, attributionFields } from "@/lib/attribution-schema";

const enquirySchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  matterType: z.enum(["conveyancing-purchase", "conveyancing-sale", "wills-estates", "elder-law", "other"]),
  propertyAddress: z.string().trim().max(250).optional().or(z.literal("")),
  message: z.string().trim().min(10, "Please give us a little detail").max(4000),
  heardAbout: z.string().trim().max(120).optional().or(z.literal("")),
  suburb: z.string().trim().max(120).optional().or(z.literal("")),
  gclid: z.string().trim().max(200).optional().or(z.literal("")),
  keyDate: z.string().trim().max(40).optional().or(z.literal("")),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
  ...attributionFields,
});

const SERVICE_LABELS: Record<EnquiryInput["matterType"], string> = {
  "conveyancing-purchase": "Conveyancing — purchase",
  "conveyancing-sale": "Conveyancing — sale",
  "wills-estates": "Wills & estates",
  "elder-law": "Elder law / power of attorney",
  other: "Other",
};

export type EnquiryInput = z.infer<typeof enquirySchema>;

export const submitEnquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => enquirySchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { buildMatterRecord, matterRecordEmailDetails } = await import("@/lib/matter-intake.server");

    // Triage first so the stored row and the notification email both carry the
    // structured matter record. Returns null on any failure — never blocks.
    const record = await buildMatterRecord({
      name: data.name,
      email: data.email,
      phone: data.phone,
      selectedMatterType: data.matterType,
      propertyAddress: data.propertyAddress,
      message: data.message,
      sourcePage: data.sourcePage,
    });

    const { error } = await supabaseAdmin.from("enquiries").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      matter_type: data.matterType,
      property_address: data.propertyAddress || null,
      message: data.message,
      source_page: data.sourcePage || null,
      heard_about: data.heardAbout || null,
      suburb: data.suburb || null,
      gclid: data.gclid || null,
      ai_summary: record?.summary || null,
      ai_matter_type: record?.matterType || null,
      ai_urgency: record?.urgency || null,
      ai_key_dates: record?.keyDates || null,
      ai_property_address: record?.propertyAddress || null,
      ai_next_actions: record?.nextActions || null,
      ai_missing_info: record?.missingInfo || null,
      ai_conflict_names: record?.parties || null,
      ai_processed_at: record ? new Date().toISOString() : null,
      ...attributionColumns(data),
    });

    if (error) {
      console.error("Failed to store enquiry", error);
      throw new Error("We could not send your enquiry. Please call us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "enquiry",
      name: data.name,
      email: data.email,
      phone: data.phone,
      matterType: data.matterType,
      sourcePage: data.sourcePage,
      message: data.message,
      details: {
        service: SERVICE_LABELS[data.matterType],
        suburb: data.suburb,
        key_date: data.keyDate,
        property_address: data.propertyAddress,
        heard_about: data.heardAbout,
        ...matterRecordEmailDetails(record),
      },
    });


    return { ok: true } as const;
  });
